import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import { Observable, OperatorFunction, pipe } from 'rxjs';
import {ConfigurationService} from '../../../../entry-routes/configuration/configuration.service';
import {Aggregate} from '../../../models/aggregate';
import {AggregateOptions} from '../../../models/aggregate-request-params';
import {CdxTask} from '../../../models/cdx-task';
import {EsPage} from '../../../models/es-page';
import {InternalRoutes} from '../../../models/internal-routes';
import {ObjectType} from '../../../models/ObjectType';
import {SearchItem} from '../../../models/search-item';
import {SearchPath} from '../../../models/search-path';
import {Url} from '../../../models/url';
import {ThesaurusService} from '../../../services/thesaurus/thesaurus.service';
import {TaskDetailsService} from '../../details/service/task/task-details.service';
import {WkfCfgFieldService} from '../../field/service/wkf/wkf-cfg-field.service';
import {AbstractFilterService} from '../../filter/service/abstract-filter.service';
import {TaskFilterService} from '../../filter/service/task-filter.service';
import {SearchResultPaginationService} from '../../search-result-pagination/service/search-result-pagination.service';
import {TaskFilterDatas, TaskFilterState} from '../../task-search/task-filter/reducer/task-filter.reducer';
import {selectTaskFilterDatas, selectTaskFilterFeature} from '../../task-search/task-filter/task-filter-selectors';
import {selectTaskSearchItemsDatas} from '../../task-search/task-search-item/task-search-item-selectors';
import {TaskSearchResultAction} from '../../task-search/task-search-result/action/task-search-result.action';
import {
  selectTaskSearchResultDatas,
  selectTaskSearchResultFeature
} from '../../task-search/task-search-result/task-search-result-selectors';
import {AbstractSearchResultService} from './abstract-search-result.service';
import {TaskSearchResultState} from '../../task-search/task-search-result/reducer/task-search-result.reducer';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import { Page } from '../../../models/page';
import {map} from 'rxjs/operators';
import { CurrentContextService } from '../../current-context/service/current-context.service';
import * as _ from 'lodash';
import { Metadata } from '../../../models/metadata';
import { AuthenticationService } from '../../../../entry-routes/authentication/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class TaskSearchResultService extends AbstractSearchResultService {
  public static readonly TASK_PAGE_DEFAULT_SORT_KEY = 'cdx_creation_date';
  public static readonly TASK_PAGE_DEFAULT_SORT_DIRECTION = 'asc';

  taskSearchItems$ = this.store.select(selectTaskSearchItemsDatas);
  taskFilterDatas$ = this.store.select(selectTaskFilterDatas);
  taskSearchListResult$ = this.store.select(selectTaskSearchResultDatas);
  private taskSearchResultState: TaskSearchResultState;
  private taskFilterState: TaskFilterState;

  private filterService: AbstractFilterService;

  static defaultPage  =new Page<CdxTask>([], 0, 0, 10, 0, false, true, {
    pageNumber: 0,
    pageSize: 10,
    sort: { active: "createTime", direction: "desc"}
  } );

  public forceRefresh : boolean;

  constructor(
    http: HttpClient,
    fieldService: WkfCfgFieldService, // TODO change to WkfCfgFieldService
    thesaurusService: ThesaurusService,
    documentFilterService: TaskFilterService,
    protected searchResultPaginationService: SearchResultPaginationService,
    private taskSearchResultAction: TaskSearchResultAction,
    private configAction: ConfigurationService,
    private taskDetailsService: TaskDetailsService,
    store: Store,
    private currentContextService: CurrentContextService,
    authenticationService: AuthenticationService
  ) {
    super(
      http,
      fieldService,
      thesaurusService,
      store,
      authenticationService
    );
    this.taskDetailsService.setTaskSearchResultService(this);
    this.thesaurusService.setTaskSearchResultService(this);
    this.filterService = documentFilterService;

    this.store.select(selectTaskSearchResultFeature)
      .pipe(takeUntilDestroyed())
      .subscribe(taskSearchResultState => this.taskSearchResultState = taskSearchResultState);

    this.store.select(selectTaskFilterFeature)
      .pipe(takeUntilDestroyed())
      .subscribe(taskFilterState => this.taskFilterState = taskFilterState);
  }

  public getForceRefresh(){
    return this.forceRefresh;
  }

  public setForceRefresh(forceRefresh : boolean){
     this.forceRefresh=forceRefresh;
  }

  public getServiceType(): InternalRoutes {
    return InternalRoutes.TASKS;
  }

  public getObjectType(): ObjectType {
    return ObjectType.TASK;
  }

  public getSearchItem$(): Observable<SearchItem[]> {
    return this.taskSearchItems$;
  }

  public getFilterDatas$(): Observable<TaskFilterDatas> {
    return this.taskFilterDatas$;
  }

  public getSearchListResult$(): Observable<EsPage<CdxTask>> {
    return this.taskSearchListResult$;
  }

  public getSearchBaseUrl(): string {
    return Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.TASKS + Url.SEARCH;
  }

  public getAggBaseUrl(): string {
    return Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.TASKS + Url.AGGS;
  }

  public getEmptyEspage(): EsPage<CdxTask> {
    return new EsPage<CdxTask>(0, [], 0, 0, [], false);
  }

  public searchDrafts(pageNumber, keepLastAgg): void {
    console.log('task-search-result-servcice searchDrafts() pageNumber:'+pageNumber+' keepLastAgg:'+keepLastAgg)
    return;
  }

  public search(): void {
    // check for lastAgg if exists for cdx_type
    // this.filterService.removeAllFilters();

    if (!this.taskFilterState || !this.taskFilterState.datas || !this.taskFilterState.datas.lastAgg) {
      this.filterService.removeAllFilters();
    } else {
      const searchPath: SearchPath = new SearchPath(this.taskFilterState.datas.lastAgg.path);
      if (searchPath.code !== SearchPath.META_CDX_TYPE) {
        this.filterService.removeAllFilters();
      } else {
        this.filterService.removeFiltersAndKeepLastAgg();
      }
    }

    this.taskDetailsService.remove();
    this.taskSearchResultAction.loadTasksStarted();
    try {
      // this.httpClient.get<any>(this.getApiMockUrl()).subscribe((page: EsPage<CdxTask>) => {
      //   this.cfgFieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
      //   this.taskSearchResultAction.loadTasksSucceeded(page as EsPage<CdxTask>);
      // });
      super._search().subscribe((page: EsPage<CdxTask>) => {

        console.debug("taskSearchResultService search() called  and super._search() result begin ")

          // FIXME remove aggs that are mocked
          // if (page.content.length) {
          //   page.aggs = [...page.aggs, ...this.getMockedAggs()];
          // }
          // load unknown fields
          this.cfgFieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
          if (!!this.taskFilterState && !!this.taskFilterState.datas && !!this.taskFilterState.datas.lastAgg) {
            const lastAggSearchPath: SearchPath = new SearchPath(this.taskFilterState.datas.lastAgg.path);
            if (lastAggSearchPath.isMeta && lastAggSearchPath.code === SearchPath.META_CDX_TYPE) {
              page.aggs = page.aggs.map((aggregate: Aggregate) => {
                if (aggregate.path === this.taskFilterState.datas.lastAgg.path) {
                  return this.taskFilterState.datas.lastAgg;
                }
                return aggregate;
              });
            }
            this.filterService.removeAllFilters();
          }

          // send result to store
          this.taskSearchResultAction.loadTasksSucceeded(page as EsPage<CdxTask>);

          console.debug("taskSearchResultService search() called  and super._search() result end (after loadTasksSucceeded)")

        },
        (error: HttpErrorResponse) => {
          this.taskSearchResultAction.loadTasksFailed(error);

          // si plantage cela peut être du au tri, on le retire du store pour pouvoir relancer la recherche sans
          this.searchResultPaginationService.reinitSearchResultPagination();
        }
      );
    } catch (error) {
      this.taskSearchResultAction.loadTasksFailed(error);
    }

  }

  public setNewPageToStore(page: EsPage<CdxTask>): void {
    this.filterService.removeAllFilters();
    this.taskDetailsService.remove();
    this.cfgFieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
    // send result to store
    this.taskSearchResultAction.loadTasksSucceeded(page as EsPage<CdxTask>);
  }

  // private getMockedAggs(): Aggregate[] {
  //   const agg1: Aggregate = new Aggregate('cdx_datas.fd_num_avis', null, [new Bucket('AvisEcheance_5384', 1)], 1);
  //   const agg2: Aggregate = new Aggregate('cdx_datas.fd_montant_ht', null, [new Bucket('153.0', 1)], 1);
  //   agg2.interval = '1.0';
  //   agg2.min = 153.0;
  //   agg2.max = 203.0;
  //   return [agg1, agg2];
  // }


  public filter(keepLastAgg = false): void {
    this.taskDetailsService.remove();
    this.taskSearchResultAction.loadTasksStarted();

    try {
      super._filter()
        .subscribe((page: EsPage<CdxTask>) => {
            // load unknown fields
            // this.fieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
            if (!!this.taskFilterState && !!this.taskFilterState.datas && !!this.taskFilterState.datas.lastAgg && keepLastAgg) {
              page.aggs = page.aggs.map((aggregate: Aggregate) => {
                if (   aggregate.path           === this.taskFilterState.datas.lastAgg.path
                    || aggregate.path + '.code' === this.taskFilterState.datas.lastAgg.path) {
                  // cas particulier task_def et process_def : le .code est ajouté lors de l'appel au filtre,
                  // donc on teste aussi -> "cdx_wkf.cdx_process.task_def.code" et "cdx_wkf.cdx_process.process_def.code"
                  // pour récupérer / conserver les autres buckets apres filtre
                  return this.taskFilterState.datas.lastAgg;
                }

                return aggregate;
              });
            }
            // send result to store
            this.taskSearchResultAction.loadTasksSucceeded(page as EsPage<CdxTask>);
          }, (error: HttpErrorResponse) => {
            this.taskSearchResultAction.loadTasksFailed(error);
          }
        );
    } catch (error) {
      this.taskSearchResultAction.loadTasksFailed(error);
    }
  }

  public gotoPage(pageNum: number): void {
    this.taskDetailsService.remove();
    this.taskSearchResultAction.updateTasksStarted();
    try {
      super._gotoPage(pageNum)
        .subscribe((page: EsPage<CdxTask>) => {
            // load unknown fields
            this.cfgFieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
            if (!!this.taskFilterState && !!this.taskFilterState.datas && !!this.taskFilterState.datas.lastAgg) {
              page.aggs = page.aggs.map((aggregate: Aggregate) => {
                if (aggregate.path === this.taskFilterState.datas.lastAgg.path) {
                  return this.taskFilterState.datas.lastAgg;
                }
                return aggregate;
              });
            }
            this.taskSearchResultAction.updateTasksSucceeded(page as EsPage<CdxTask>);
          }, (error: HttpErrorResponse) => {
            this.taskSearchResultAction.updateTasksFailed(error);
          }
        );
    } catch (error) {
      this.taskSearchResultAction.updateTasksFailed(error);
    }
  }

  public loadAggregate(path: string, opts: AggregateOptions, checkPath = false): void {
    this.taskSearchResultAction.loadAggregateStart();
    try {
      this._loadAggregate(path, opts, checkPath)
        .subscribe((agg: Aggregate) => {
            // this.fieldService.loadFieldsFromPath(path);
            this.taskSearchResultAction.loadAggregateSucceeded(agg);
          },
          (error: HttpErrorResponse) => {
            this.taskSearchResultAction.loadAggregateFailed(error);
          });
    } catch (error) {
      this.taskSearchResultAction.loadAggregateFailed(error);
    }
  }

  public workspaceParams(): OperatorFunction<HttpParams, HttpParams> {
    return super.workspaceParams();
  }

  public coordParams(limit: number = AbstractSearchResultService.TASK_PAGE_SIZE, offset = 0): OperatorFunction<HttpParams, HttpParams> {
    return super.coordParams(limit, offset);
  }

  public searchItemsParams(pathToCheck: any = null): OperatorFunction<HttpParams, HttpParams> {
    return super.searchItemsParams(pathToCheck);
  }

  public filterItemsParams(path: any = null): OperatorFunction<HttpParams, HttpParams> {
    const currentUserId = this.authenticationService.getUserCredentials().id;
    return pipe(super.filterItemsParams(path),
      map(httpParams => {
        let newHttpParams = new HttpParams();
        httpParams.keys().forEach(path => {

          const values : string[] = httpParams.getAll(path);
          values.forEach( (value) => {
              if (path !== "cdx_wkf.assignation") {
                  newHttpParams = newHttpParams.append( path, value );
              } else {
                  newHttpParams = newHttpParams.append(path + '.' + value, currentUserId);
              }
          });

        })
        return newHttpParams;
      }
      ));
  }

  getTaskSearchResultState(): TaskSearchResultState {
    return this.taskSearchResultState;
  }

  // surcharge pour pouvoir récupérer les valeurs de tri par défaut de la config provenant de la bdd
  protected sortByKeyAndDirection(): OperatorFunction<HttpParams, HttpParams> {
    let res = super.sortByKeyAndDirection();

    if ( !this.sortKey && !this.sortDirection ) {
     // on va récupérer les valeurs par défaut provenant de la config des colonnes

      this.sortKey=this.currentContextService.getCurrentTaskSortKey();
      this.sortDirection=this.currentContextService.getCurrentTaskSortDirection();

      if ( !this.sortKey && !this.sortDirection ) {
        this.sortKey= TaskSearchResultService.TASK_PAGE_DEFAULT_SORT_KEY ;
        this.sortDirection= TaskSearchResultService.TASK_PAGE_DEFAULT_SORT_DIRECTION;
      }


      res= map((params) => {
        if (!!this.sortKey) {

          let sort = '';
          let order = AbstractSearchResultService.DESC;
          if (!!this.sortDirection) {
            order = this.sortDirection;
          }
          sort = this.sortKey + ':' + order;

          params = params.append('_sort', sort);
        }

        return params;
      });
    }

    return res;
  }

  updateTaskInSearchResultPage( taskInstanceId: string, meta: Metadata ) {
    const taskSearchListResult: EsPage<CdxTask> = !!this.taskSearchResultState ?
      this.taskSearchResultState.datas : null;
    if (!taskSearchListResult) {
      return;
    }
    const cloneTaskSearchListResult = _.cloneDeep(taskSearchListResult);
    const searchedTask: CdxTask = cloneTaskSearchListResult.content.find((cdxTask: CdxTask) => cdxTask.cdx_wkf.cdx_process.task_instance_id === taskInstanceId);
    if (!!searchedTask) {
      searchedTask.metadata = meta;
      this.taskSearchResultAction.updateTaskSucceeded(searchedTask);
    }
  }
}
